import React from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { ObjectShape } from 'yup/lib/object';
import {
  downloadConditionedStock,
  downloadCumulatedMonhlyReport,
  downloadGlobalReportCumulatedMaterialResult,
  downloadGlobalReportDistributionKeys,
  downloadGlobalReportMaterialResults,
  downloadGlobalReportProductionExpedition,
  downloadGlobalReportTheoreticalAndRealised,
} from 'services/apiOperations';
import { useFirstAndLastClosureDates } from 'utils/closureHooks';
import { DefaultFormValues, ReportForm } from '../types';
import DownloadCumulatedMaterialResultReportCard from '../components/DownloadCumulatedMaterialResultReportCard';
import DownloadReportCard from '../components/DownloadReportCard';
import DownloadConditionedStockReportCard from '../components/DownloadConditionedStockReportCard';
import DownloadProductionExpeditionReportCard from '../components/DownloadProductionExpeditionReportCard';

const GlobalReports: React.FC = () => {
  const closedMonthsBoundaries = useFirstAndLastClosureDates();

  const { t } = useTranslation();

  const yupSchemaShape: ObjectShape = {
    year: yup
      .date()
      .typeError(t('reports.validation.date.invalid'))
      .nullable()
      .required(t('reports.validation.required')),
    month: yup
      .date()
      .typeError(t('reports.validation.date.invalid'))
      .nullable()
      .required(t('reports.validation.required')),
  };

  const defaultDate = closedMonthsBoundaries ? closedMonthsBoundaries.lastClosureDate : null;
  const defaultFormValue: DefaultFormValues = {
    year: defaultDate,
    month: defaultDate,
  };

  const downloadReportTheoreticalAndRealised = (sortingCenterId: string, form: ReportForm): Promise<void> => {
    return downloadGlobalReportTheoreticalAndRealised(
      sortingCenterId,
      form.year.toLocaleString('default', { year: 'numeric' }),
      form.month.toLocaleString('default', { month: 'numeric' }),
      false,
    );
  };
  const downloadReportMaterialResults = (sortingCenterId: string, form: ReportForm): Promise<void> => {
    return downloadGlobalReportMaterialResults(
      sortingCenterId,
      form.year.toLocaleString('default', { year: 'numeric' }),
      form.month.toLocaleString('default', { month: 'numeric' }),
      false,
    );
  };
  const downloadReportProductionExpedition = (
    sortingCenterId: string,
    startDate: Date,
    endDate: Date,
  ): Promise<void> => {
    return downloadGlobalReportProductionExpedition(sortingCenterId, startDate, endDate, false);
  };

  const downloadReportCumulatedMaterialResult = (
    sortingCenterId: string,
    startDate: Date,
    endDate: Date,
  ): Promise<void> => {
    return downloadGlobalReportCumulatedMaterialResult(sortingCenterId, startDate, endDate);
  };

  const DownloadConditionedStockResult = (sortingCenterId: string, startDate: Date, endDate: Date): Promise<void> => {
    return downloadConditionedStock(sortingCenterId, startDate, endDate);
  };

  const downloadReportDistributionKeys = (sortingCenterId: string, form: ReportForm): Promise<void> =>
    downloadGlobalReportDistributionKeys(
      sortingCenterId,
      form.year.toLocaleString('default', { year: 'numeric' }),
      form.month.toLocaleString('default', { month: 'numeric' }),
      false,
    );

  const downloadReportCumulatedMonthly = (sortingCenterId: string, form: ReportForm): Promise<void> =>
    downloadCumulatedMonhlyReport(
      sortingCenterId,
      form.year.toLocaleString('default', { year: 'numeric' }),
      form.month.toLocaleString('default', { month: 'numeric' }),
      false,
    );

  return (
    <>
      <DownloadReportCard
        collapseTitle={t('reports.global.cumulativeTheoreticalAndRealizedProductions')}
        formId={'cumulativeTheoreticalAndRealizedProductions_report_form'}
        handleClickDownload={downloadReportTheoreticalAndRealised}
        selectClient={false}
        closedMonthsBoundaries={closedMonthsBoundaries}
        yupSchemaShape={yupSchemaShape}
        defaultFormValue={defaultFormValue}
      />
      <DownloadReportCard
        collapseTitle={t('reports.global.bilanOfResultMaterial')}
        formId={'bilanOfResultMaterial_report_form'}
        handleClickDownload={downloadReportMaterialResults}
        selectClient={false}
        closedMonthsBoundaries={closedMonthsBoundaries}
        yupSchemaShape={yupSchemaShape}
        defaultFormValue={defaultFormValue}
      />
      <DownloadCumulatedMaterialResultReportCard
        collapseTitle={t('reports.global.cumulatedMaterialsResults')}
        handleClickDownload={downloadReportCumulatedMaterialResult}
        closedMonthsBoundaries={closedMonthsBoundaries}
      />
      <DownloadReportCard
        collapseTitle={t('reports.global.cumulatedMonthlyReport')}
        formId={'cumulated_monthly_report_form'}
        handleClickDownload={downloadReportCumulatedMonthly}
        selectClient={false}
        closedMonthsBoundaries={closedMonthsBoundaries}
        yupSchemaShape={yupSchemaShape}
        defaultFormValue={defaultFormValue}
      />
      <DownloadProductionExpeditionReportCard
        collapseTitle={t('reports.global.productionExpedition')}
        handleClickDownload={downloadReportProductionExpedition}
        closedMonthsBoundaries={closedMonthsBoundaries}
      />
      <DownloadConditionedStockReportCard
        collapseTitle={t('reports.global.conditionedStockByMaterial')}
        handleClickDownload={DownloadConditionedStockResult}
        closedMonthsBoundaries={closedMonthsBoundaries}
      />
      <DownloadReportCard
        collapseTitle={t('reports.global.distributionKeys')}
        formId={'distribution_keys_report_form'}
        handleClickDownload={downloadReportDistributionKeys}
        selectClient={false}
        closedMonthsBoundaries={closedMonthsBoundaries}
        yupSchemaShape={yupSchemaShape}
        defaultFormValue={defaultFormValue}
      />
    </>
  );
};
export default GlobalReports;
